import React from "react";
import { Link } from "react-router-dom";
import { StarRating } from "./StarRating";
import VerifiedBadge from "./VerifiedBadge";
import SafeImage from "./SafeImage";
import { usePlatformConfig } from "../lib/platformConfig";
import TrustBadges from "./TrustBadges";
import FavoriteButton from "./FavoriteButton";

export default function ServiceCard({ s }) {
  const cashless = !!usePlatformConfig().cashless_balance_enabled;
  return (
    <Link
      to={`/services/${s.id}`}
      data-testid={`service-card-${s.id}`}
      className="group relative block rounded-xl border overflow-hidden hover:-translate-y-1 transition-all duration-300"
      style={{
        background: "var(--gf-surface)",
        borderColor: "var(--gf-border)",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--gf-card-hover-border)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--gf-border)")}
    >
      <FavoriteButton targetType="service" targetId={s.id} variant="card" />
      <div className="aspect-[4/3] overflow-hidden" style={{ background: "var(--gf-bg-soft)" }}>
        <SafeImage
          src={s.image_url}
          alt={s.title}
          testId={`card-cover-${s.id}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          fallback={
            <div data-testid={`card-cover-${s.id}-fallback`} className="w-full h-full flex items-center justify-center font-display text-3xl opacity-30" style={{ color: "var(--gf-text)", background: "var(--gf-bg-soft)" }}>
              Book<span style={{ color: "var(--gf-accent)" }}>Freely</span>
            </div>
          }
        />
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "var(--gf-text-muted)" }}>
          <span>{s.category}</span>
          {(() => {
            const requiresDeposit = s.requires_deposit !== false && Number(s.downpayment_amount || 0) > 0;
            if (!requiresDeposit) {
              return <span style={{ color: "var(--gf-text-muted)" }} data-testid={`deposit-tag-${s.id}`}>No deposit</span>;
            }
            return (
              <span style={{ color: s.downpayment_refundable ? "var(--gf-accent)" : "var(--gf-text-muted)" }} data-testid={`deposit-tag-${s.id}`}>
                {s.downpayment_refundable ? "Refundable deposit" : "Non-refundable deposit"}
              </span>
            );
          })()}
        </div>
        <h3 className="font-display text-xl leading-tight" style={{ color: "var(--gf-text)" }}>{s.title}</h3>
        <div className="mt-2 flex items-center gap-2" data-testid={`card-business-${s.id}`}>
          <SafeImage
            src={s.business_logo_url}
            alt=""
            className="w-7 h-7 rounded-full object-cover border flex-shrink-0"
            fallback={
              <span className="w-7 h-7 rounded-full border flex items-center justify-center text-[11px] flex-shrink-0"
                    style={{ borderColor: "var(--gf-border)", color: "var(--gf-text-muted)", background: "var(--gf-bg-soft)" }}>
                {(s.business_name || "?").charAt(0).toUpperCase()}
              </span>
            }
          />
          <span className="text-sm truncate" style={{ color: "var(--gf-text-soft)" }}>{s.business_name}</span>
        </div>
        <div className="mt-1.5 text-xs flex items-center gap-2 flex-wrap" style={{ color: "var(--gf-text-muted)" }}>
          <span data-testid={`card-duration-${s.id}`}>{s.duration_minutes} min</span>
          {(s.business_city || s.location) && (
            <span data-testid={`card-location-${s.id}`}>
              · {s.business_city ? [s.business_city, s.business_state].filter(Boolean).join(", ") : s.location}
            </span>
          )}
        </div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <StarRating value={s.avg_rating || 0} count={s.rating_count || 0} />
          {(s.business_trust_badges || []).includes("verified") && <VerifiedBadge size="xs" />}
          {typeof s.distance_miles === "number" && (
            <span data-testid={`distance-${s.id}`} className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
              {s.distance_miles < 1 ? "< 1 mi" : `${s.distance_miles.toFixed(1)} mi`}
              {s.business_city ? ` · ${s.business_city}` : ""}
            </span>
          )}
          {s.offers_online && (
            <span className="text-[10px] uppercase tracking-[0.2em] rounded-full px-2 py-0.5" style={{ background: "var(--gf-bg-soft)", color: "var(--gf-text-muted)" }}>
              Online available
            </span>
          )}
          {s.service_location_type && s.service_location_type !== "at_business" && s.business_mobile_verified && (
            <span data-testid={`card-mv-badge-${s.id}`} className="text-[10px] uppercase tracking-[0.2em] rounded-full px-2 py-0.5"
                  style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
              Mobile-safe
            </span>
          )}
        </div>
        {Array.isArray(s.business_trust_badges) && s.business_trust_badges.length > 0 && (
          <TrustBadges badges={s.business_trust_badges.filter((b) => b !== "verified")} size="xs" className="mt-2" />
        )}
        <p className="text-sm mt-2 line-clamp-2" style={{ color: "var(--gf-text-muted)" }}>{s.description}</p>
        <div className="mt-4 flex items-center justify-between border-t pt-4" style={{ borderColor: "var(--gf-border)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Price</div>
            <div className="font-display text-lg" style={{ color: "var(--gf-text)" }}>${Number(s.price || 0).toFixed(2)}</div>
          </div>
          <div className="text-right">
            {(() => {
              const requiresDeposit = s.requires_deposit !== false && Number(s.downpayment_amount || 0) > 0;
              if (!requiresDeposit) {
                return (
                  <>
                    <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }} data-testid={`card-balance-label-${s.id}`}>
                      {cashless ? "Card charged after appointment" : "Due at appointment"}
                    </div>
                    <div className="font-display text-lg" style={{ color: "var(--gf-text)" }} data-testid={`card-deposit-${s.id}`}>
                      ${Number(s.price || 0).toFixed(2)}
                    </div>
                  </>
                );
              }
              const dollar = s.deposit_type === "percent"
                ? +(Number(s.price || 0) * Number(s.downpayment_amount || 0) / 100).toFixed(2)
                : +Number(s.downpayment_amount || 0).toFixed(2);
              return (
                <>
                  <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Deposit</div>
                  <div className="font-display text-lg" style={{ color: "var(--gf-accent)" }} data-testid={`card-deposit-${s.id}`}>
                    ${dollar.toFixed(2)}
                    {s.deposit_type === "percent" && (
                      <span className="text-xs ml-1" style={{ color: "var(--gf-text-muted)" }}>({Number(s.downpayment_amount).toFixed(0)}%)</span>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </Link>
  );
}
